import React from 'react'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import type {InterpreterAiQcSortField} from './interpreterAiQcApi'

const PILL_TEXT = '#ffffff'
const PASS_TEXT = '#a3a242'
const FAIL_TEXT = '#d7383c'
const PASS_BG = '#a3a242'
const FAIL_BG = '#d7383c'

export type InterpreterAiQcTableRow = {
  callHeaderId: number
  callId: string
  contactId: string
  customerName: string
  requesterName: string
  aiQcStatusPass: boolean
  nameMentioned: boolean
  languageMentioned: boolean
  idMentioned: boolean
  callDateTime: string
}

type Props = {
  rows: InterpreterAiQcTableRow[]
  currentPage: number
  totalPages: number
  rowsPerPage: number
  totalItems: number
  sortBy: InterpreterAiQcSortField
  sortOrder: 'asc' | 'desc'
  onSort: (field: InterpreterAiQcSortField) => void
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
  onCallIdClick: (row: InterpreterAiQcTableRow) => void
  formatCallDateTime: (iso: string) => string
}

const StatusBadge: React.FC<{pass: boolean}> = ({pass}) =>
  pass ? (
    <span
      className='badge fw-semibold text-center'
      style={{backgroundColor: PASS_BG, color: PILL_TEXT, padding: '6px 12px', borderRadius: 20}}
    >
      Pass
    </span>
  ) : (
    <span
      className='badge fw-semibold text-center'
      style={{backgroundColor: FAIL_BG, color: PILL_TEXT, padding: '6px 12px', borderRadius: 20}}
    >
      Fail
    </span>
  )

const ChecklistItem: React.FC<{label: string; mentioned: boolean}> = ({label, mentioned}) => (
  <span className='d-inline-flex align-items-center me-3 fs-8'>
    <i
      className={`bi ${mentioned ? 'bi-check-circle' : 'bi-x-circle'} me-1`}
      style={{color: mentioned ? PASS_TEXT : FAIL_TEXT}}
    ></i>
    {label}: {mentioned ? 'mentioned' : 'missed'}
  </span>
)

const InterpreterAiQcTable: React.FC<Props> = ({
  rows,
  currentPage,
  totalPages,
  rowsPerPage,
  totalItems,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  onRowsPerPageChange,
  onCallIdClick,
  formatCallDateTime,
}) => {
  const SortHeader = ({
    field,
    label,
    className = '',
  }: {
    field: InterpreterAiQcSortField
    label: string
    className?: string
  }) => (
    <th className={className}>
      <button
        type='button'
        className='btn btn-link p-0 text-start text-muted text-uppercase fw-semibold text-decoration-none'
        onClick={() => onSort(field)}
      >
        {label}
        {sortBy === field && <span className='ms-1'>{sortOrder === 'asc' ? ' ↑' : ' ↓'}</span>}
      </button>
    </th>
  )

  return (
    <>
      <div className='table-responsive' style={{minHeight: '28vh'}}>
        <table className='table table-row-dashed table-row-gray-300 table-hover align-middle gs-2 gy-2'>
          <thead>
            <tr className='fw-semibold text-muted text-uppercase'>
              <SortHeader field='callid' label='Call ID' className='min-w-125px ps-4' />
              <SortHeader field='clientname' label='Client Name' className='min-w-200px' />
              <SortHeader field='aiqcstatus' label='AI QC Status' className='min-w-100px' />
              <SortHeader field='qcchecklist' label='QC Checklist Result' className='min-w-300px' />
              <SortHeader field='calldatetime' label='Call Date Time' className='min-w-175px pe-4' />
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <tr key={row.callHeaderId}>
                  <td className='ps-4'>
                    <a
                      href='#'
                      className='text-gray-800 text-hover-primary fw-semibold d-block fs-6'
                      onClick={(e) => {
                        e.preventDefault()
                        onCallIdClick(row)
                      }}
                    >
                      {row.callId || '—'}
                    </a>
                  </td>
                  <td>
                    <div className='fw-bold text-dark'>{row.customerName || '—'}</div>
                    {row.requesterName && (
                      <div className='text-muted fs-8'>{row.requesterName}</div>
                    )}
                  </td>
                  <td>
                    <StatusBadge pass={row.aiQcStatusPass} />
                  </td>
                  <td>
                    <ChecklistItem label='Name' mentioned={row.nameMentioned} />
                    <ChecklistItem label='Lang' mentioned={row.languageMentioned} />
                    <ChecklistItem label='ID' mentioned={row.idMentioned} />
                  </td>
                  <td className='pe-4 text-muted fs-6'>{formatCallDateTime(row.callDateTime)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>
                  <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                    <div className='text-center'>
                      <div className='symbol symbol-200px'>
                        <img src='/media/other/nodata.png' alt='' />
                      </div>
                    </div>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                      No matching records found
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <CommonPaginationModel
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        total={totalItems}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  )
}

export default InterpreterAiQcTable
